import React, { useState, useEffect, useRef } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../hooks/useRedux';
import { PENSION_MIN_SEK, getPensionMaxSek, canContributePension } from '../utils/calculation.utils';

interface PensionData {
  [freelancerId: string]: {
    pension: boolean;
    pensionAmount: number;
  };
}

interface HolidayData {
  [freelancerId: string]: {
    holidaySavings: boolean;
    holidaySavingsFee: number;
  };
}

interface PensionHolidaySectionProps {
  freelancerId: string;
  freelancerName?: string;
  grossSalary: number;
  isRegularEngagement: boolean;
  globalData?: any;
  t: (key: string) => string;
  pensionData: PensionData;
  holidayData: HolidayData;
  onPensionChange: (data: PensionData) => void;
  onHolidayChange: (data: HolidayData) => void;
  calculatedAmount?: number;
}

const PensionHolidaySection: React.FC<PensionHolidaySectionProps> = ({
  freelancerId,
  freelancerName,
  grossSalary,
  isRegularEngagement,
  globalData,
  t: propT,
  pensionData,
  holidayData,
  onPensionChange,
  onHolidayChange,
  calculatedAmount
}) => {
  // Use translation hook
  const { t, i18n } = useTranslation();
  const { freelancers } = useAppSelector((state: any) => state?.customer) || {};
  const freelancerObj = freelancers?.find(
    (freelancer: any) => freelancer?.userId?.toString() === freelancerId
  );
  const isPensionAllowed = freelancerObj?.isPensionAllowed ?? true;

  // Data used to reconstruct WHY pension is unavailable (backend only sends the
  // isPensionAllowed boolean, so we explain it from the freelancer's own fields).
  // The freelancer's profile fields (country, ssn) live under `signUpUser`.
  const freelancerProfile = freelancerObj?.signUpUser || freelancerObj || {};
  const freelancerCountry = (freelancerProfile?.country ?? freelancerObj?.country ?? '').toString().trim();
  const hasCountry = freelancerCountry.length > 0;
  const isSweden =
    freelancerCountry.toLowerCase() === 'sweden' ||
    freelancerCountry.toUpperCase() === 'SE' ||
    freelancerCountry.toLowerCase() === 'sverige';
  // FIRST gate: the freelancer must be based in Sweden. If we know the country and
  // it isn't Sweden, pension is unavailable — checked before the 5000 SEK / age rules.
  const isNotSweden = hasCountry && !isSweden;

  // Swedish SSN is 12 digits; last 4 = "0000" means it's not a valid personnummer.
  const freelancerSsnDigits = (freelancerProfile?.ssn ?? freelancerObj?.ssn ?? '').toString().replace(/\D/g, '');
  const ssnInvalid = freelancerSsnDigits.length === 12 && freelancerSsnDigits.slice(-4) === '0000';

  // Occupational pension (Induli): min 1,000 SEK, max = min(49,000, 25% of gross).
  // Gross = the already-calculated per-freelancer value passed in as `calculatedAmount`.
  const pensionGross = Number(calculatedAmount || 0);
  const pensionMax = getPensionMaxSek(pensionGross);
  const canContribute = canContributePension(pensionGross);

  const [showErrorTooltip, setShowErrorTooltip] = useState(false);
  const [pensionHasBeenSelected, setPensionHasBeenSelected] = useState(false);
  const errorTooltipTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Get current values from state
  const currentPension = pensionData[freelancerId] || {
    pension: false,
    pensionAmount: PENSION_MIN_SEK
  };

  const currentHoliday = holidayData[freelancerId] || {
    holidaySavings: false,
    holidaySavingsFee: globalData?.holidaySavingsFee?.[0]?.globalValue || 12
  };

  // Check if calculated amount meets pension requirement (5000 SEK minimum)
  const isAmountValid = Number(calculatedAmount || 0) >= 5000.00;

  // First failing check wins. Country (Sweden) is now checked FIRST, then:
  // 2) gross < 5000, 3) invalid SSN, 4) not a regular engagement,
  // 5) fallback → freelancer is above 66.
  const pensionDisabledReason = isNotSweden
    ? (t('PENSION_DISABLED_COUNTRY') || 'Pension is only available for freelancers based in Sweden.')
    : !isAmountValid
    ? (t('PENSION_ERROR_GROSS_SALARY') || 'The pension option is only available if the gross salary is 5000 SEK or more.')
    : ssnInvalid
    ? (t('PENSION_DISABLED_SSN') || 'Pension requires a valid Swedish personal number (SSN) for this freelancer.')
    : !isRegularEngagement
    ? (t('AVAILABLE_ONLY_REGULAR_ENGAGEMENT') || 'Available only for Regular/Continuous engagement type.')
    : (t('PENSION_DISABLED_AGE') || 'Pension is not available because this freelancer is above 66 years of age.');

  // Auto-disable pension and show error when calculated amount drops below 5000.
  //  • pensionHasBeenSelected  → only after the USER interacts, so fetched/edit
  //    data on load is never auto-disabled (fixes pension not showing on GET).
  //  • currentPension.pension  → after we disable it below, this becomes false
  //    so the effect can't re-fire in a loop (onPensionChange creates a new
  //    pensionData object, which is a dependency of this effect).
  useEffect(() => {
   if (pensionHasBeenSelected && currentPension.pension && !isAmountValid && calculatedAmount !== undefined) {
      // Clear any existing timer
      if (errorTooltipTimerRef.current) {
        clearTimeout(errorTooltipTimerRef.current);
      }

      // Show error tooltip
      setShowErrorTooltip(true);

      // Auto-disable pension and reset amount
      onPensionChange({
        ...pensionData,
        [freelancerId]: {
          pension: false,
          pensionAmount: PENSION_MIN_SEK
        }
      });

      // Set timer to hide tooltip after 5 seconds
      errorTooltipTimerRef.current = setTimeout(() => {
        setShowErrorTooltip(false);
      }, 3000);
    }
  }, [isAmountValid, calculatedAmount, currentPension.pension, freelancerId, onPensionChange, pensionData]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (errorTooltipTimerRef.current) {
        clearTimeout(errorTooltipTimerRef.current);
      }
    };
  }, []);

  const handlePensionCheckboxChange = (checked: boolean) => {
    if (checked) {
      setPensionHasBeenSelected(true);

      // Show error if trying to enable with insufficient calculated amount
      if (!isAmountValid) {
        if (errorTooltipTimerRef.current) {
          clearTimeout(errorTooltipTimerRef.current);
        }
        setShowErrorTooltip(true);
        errorTooltipTimerRef.current = setTimeout(() => {
          setShowErrorTooltip(false);
        }, 3000);
        // Don't enable pension if amount is invalid
        return;
      }
    }

    // Default to the 1,000 SEK minimum contribution if no amount is set yet.
    const defaultAmount = currentPension.pensionAmount || PENSION_MIN_SEK;
    
    onPensionChange({
      ...pensionData,
      [freelancerId]: {
        ...currentPension,
        pension: checked,
        pensionAmount: checked ? defaultAmount : 0
      }
    });

    // On mobile, scroll the just-revealed Pension amount dropdown into view.
    scrollFeeDropdownIntoView('pension', checked);
  };

  // Bring the revealed Pension/Holiday amount dropdown (rendered in the summary
  // box) into view on mobile so the user doesn't have to hunt for it.
  const scrollFeeDropdownIntoView = (kind: 'pension' | 'holiday', checked: boolean) => {
    if (!checked || typeof window === 'undefined' || window.innerWidth > 991) return;
    window.setTimeout(() => {
      const el = document.getElementById(`iv-${kind}-fee-${freelancerId}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 250);
  };

  const handlePensionAmountChange = (amount: number) => {
    setPensionHasBeenSelected(true);

    // Show error tooltip if calculated amount is insufficient
    if (!isAmountValid) {
      if (errorTooltipTimerRef.current) {
        clearTimeout(errorTooltipTimerRef.current);
      }
      setShowErrorTooltip(true);
      errorTooltipTimerRef.current = setTimeout(() => {
        setShowErrorTooltip(false);
      }, 3000);
    }

    onPensionChange({
      ...pensionData,
      [freelancerId]: {
        ...currentPension,
        pensionAmount: amount
      }
    });
  };

  const handleHolidayCheckboxChange = (checked: boolean) => {
    // When enabling, ensure a valid fee is set (edit-load hydrates it to 0 when
    // holiday was originally off; without this the payload sends fee: 0).
    const feeToSet = checked
      ? (currentHoliday.holidaySavingsFee || globalData?.holidaySavingsFee?.[0]?.globalValue || 12)
      : currentHoliday.holidaySavingsFee;
    onHolidayChange({
      ...holidayData,
      [freelancerId]: {
        ...currentHoliday,
        holidaySavings: checked,
        holidaySavingsFee: feeToSet
      }
    });

    // On mobile, scroll the just-revealed Holiday fee dropdown into view.
    scrollFeeDropdownIntoView('holiday', checked);
  };

  const handleHolidayFeeChange = (fee: number) => {
    onHolidayChange({
      ...holidayData,
      [freelancerId]: {
        ...currentHoliday,
        holidaySavingsFee: fee
      }
    });
  };

  // ── Whole-card click target ────────────────────────────────────────────────
  // Clicking anywhere in a Pension / Holiday card toggles that option, not just
  // the checkbox and its label. Clicks landing on `.form-check` are skipped: the
  // native <input> + <label htmlFor> pair already toggles there, so handling them
  // here too would fire twice and cancel itself out.
  // The `enabled` flag mirrors each checkbox's own `disabled` rule, so a card the
  // user can't act on stays inert.
  const cardToggleHandler = (enabled: boolean, toggle: () => void) => (e: React.MouseEvent) => {
    if (!enabled) return;
    if ((e.target as HTMLElement | null)?.closest('.form-check')) return;
    toggle();
  };

  // Pension can only be switched on when the salary also allows at least the
  // 1,000 SEK minimum contribution (25% of gross ≥ 1,000, i.e. gross ≥ 4,000).
  const pensionToggleEnabled = isRegularEngagement && isAmountValid && canContribute;
  const pensionIsChecked = pensionToggleEnabled && currentPension.pension;
  const holidayIsChecked = isRegularEngagement && currentHoliday.holidaySavings;

  return (
    <>
      <style>{`
        .pension-holiday-section input[type="checkbox"].form-check-input {
          width: 20px;
          height: 20px;
          border: 2px solid #d1d5db;
          border-radius: 4px;
          background-color: transparent;
          cursor: pointer;
          position: relative;
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        
        .pension-holiday-section input[type="checkbox"].form-check-input:checked {
          background-color: transparent;
          border-color: #6750b8;
        }
        
        .pension-holiday-section input[type="checkbox"].form-check-input:checked::after {
          content: '✓';
          color: #6750b8;
          font-size: 16px;
          font-weight: bold;
          line-height: 1;
          margin-top: -2px;
        }
        
        .pension-holiday-section input[type="checkbox"].form-check-input:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pension-holiday-section .ph-pension-note {
          display: flex;
          flex-direction: column;
          gap: 4px;
          margin-top: 4px;
        }
        .pension-holiday-section .ph-note-text {
          white-space: break-spaces;
          color: #252B37;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 13.5px;
          font-weight: 300;
          line-height: 1.45;
          letter-spacing: 0.07px;
        }
        .pension-holiday-section .ph-note-text--warn { color: #b45309; font-weight: 500; }
        .pension-holiday-section .ph-note-limits {
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 12.5px;
          font-weight: 700;
          color: #6b4fc7;
        }
        .pension-holiday-section .ph-note-limits-sub { font-weight: 400; color: #8b84a6; }
        .pension-holiday-section .ph-note-readmore {
          display: inline-flex;
          align-items: center;
          gap: 3px;
          width: fit-content;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 12.5px;
          font-weight: 700;
          color: #7c3aed;
          text-decoration: none;
        }
        .pension-holiday-section .ph-note-readmore:hover { text-decoration: underline; }
      `}</style>
      <div className="pension-holiday-section">
        <div className="col-custom d-block align-items-center justify-content-between pr-0">
          {/* Pension Section — disabled (with reason) when the backend disallows it
              OR the freelancer is not based in Sweden (checked first). */}
          {(isPensionAllowed && !isNotSweden) ? (
          <OverlayTrigger
            show={showErrorTooltip && !isAmountValid && pensionHasBeenSelected}
            trigger={[]}
            placement="top"
            overlay={
              <Popover
                id={`pension-error-popover-${freelancerId}`}
                style={{ margin: 0 }}
              >
                <Popover.Body
                  style={{
                    backgroundColor: '#dc3545',
                    color: 'white',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    fontSize: '13px',
                    fontWeight: '500',
                    boxShadow: '0 4px 12px rgba(220, 53, 69, 0.3)',
                    border: 'none'
                  }}
                >
                  {t('PENSION_ERROR_GROSS_SALARY')}
                </Popover.Body>
              </Popover>
            }
          >
          <div
            className={`ph-card${pensionToggleEnabled ? ' ph-card--clickable' : ''}`}
            data-tour="tour-pension"
            onClick={cardToggleHandler(pensionToggleEnabled, () => handlePensionCheckboxChange(!pensionIsChecked))}
          >
            <div className="form-check d-flex align-items-center mr-2">
              <input
                className={`form-check-input text-dark ${(!isRegularEngagement || !isAmountValid) ? '' : 'cursor-pointer'}`}
                style={(!isRegularEngagement || !isAmountValid) ? { cursor: 'not-allowed' } : undefined}
                type="checkbox"
                id={`pension-${freelancerId}`}
                // Pension stays disabled unless the salary allows the 1,000 SEK
                // minimum contribution, and only for regular engagements.
                disabled={!isRegularEngagement || !isAmountValid || !canContribute}
                checked={isRegularEngagement && isAmountValid && canContribute && currentPension.pension}
                onChange={(e) => handlePensionCheckboxChange(e.target.checked)}
              />
              <label
                htmlFor={`pension-${freelancerId}`}
                className="form-check-label pl-2 mt-1 cursor-pointer"
              >
                <p style={{
                  color: '#252B37',
                  fontFamily: '"Plus Jakarta Sans"',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 600,
                  lineHeight: '140%',
                  margin: 0,
                  display: 'inline'
                }}>
                  {t('PENSION')}
                </p>
                {freelancerName && (
                  <span className="ml-2"  style={{
                  color: '#252B37',
                  fontFamily: '"Plus Jakarta Sans"',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 600,
                  lineHeight: '140%',
                  margin: 0,
                  display: 'inline'
                }}>({freelancerName})</span>
                )}
              </label>
            </div>
            <div className="ph-pension-note">
              {!isRegularEngagement ? (
                <span className="ph-note-text">{t('AVAILABLE_ONLY_REGULAR_ENGAGEMENT')}</span>
              ) : !isAmountValid ? (
                <span className="ph-note-text">{t('PENSION_NOTE_5000_SEK')}</span>
              ) : !canContribute ? (
                <span className="ph-note-text ph-note-text--warn">
                  {(t('PENSION_MIN_NOT_MET_MSG') || 'Your salary allows up to {max} SEK (25% of gross), which is below the 1,000 SEK minimum — a pension contribution can’t be added.').replace('{max}', pensionMax.toLocaleString())}
                </span>
              ) : (
                <>
                  <span className="ph-note-text">
                    {t('PENSION_INDULI_DESC') || 'Save for your future with occupational pension through Induli.'}
                  </span>
                  <span className="ph-note-limits">
                    Min {PENSION_MIN_SEK.toLocaleString()} SEK · Max {pensionMax.toLocaleString()} SEK
                    <span className="ph-note-limits-sub"> ({t('PENSION_LIMIT_HINT') || '25% of salary, up to 49,000 SEK'})</span>
                  </span>
                  {/* Client feedback: keep the invoice tool clean — detailed SPP/pension
                      info lives on the landing page behind "Read more", not inline here. */}
                  <a
                    className="ph-note-readmore"
                    href={`/pension?lang=${i18n.language || 'en-us'}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t('READ_MORE') || 'Read more'}
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </a>
                </>
              )}
            </div>
          </div>
        </OverlayTrigger>
          ) : (
          /* Pension not allowed for this freelancer — still shown, but disabled,
             with the same reason as the "Add Multiple Rows" modal. */
          <div className="ph-card ph-card--disabled" data-tour="tour-pension">
            <div className="form-check d-flex align-items-center mr-2">
              <input
                className="form-check-input text-dark"
                type="checkbox"
                id={`pension-${freelancerId}`}
                disabled
                checked={false}
                readOnly
              />
              <label
                htmlFor={`pension-${freelancerId}`}
                className="form-check-label pl-2 mt-1"
                style={{ cursor: 'not-allowed' }}
              >
                <p style={{
                  color: '#8a8397',
                  fontFamily: '"Plus Jakarta Sans"',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 600,
                  lineHeight: '140%',
                  margin: 0,
                  display: 'inline'
                }}>
                  {t('PENSION')}
                </p>
                {freelancerName && (
                  <span className="ml-2" style={{
                    color: '#8a8397',
                    fontFamily: '"Plus Jakarta Sans"',
                    fontSize: '16px',
                    fontStyle: 'normal',
                    fontWeight: 600,
                    lineHeight: '140%',
                    margin: 0,
                    display: 'inline'
                  }}>({freelancerName})</span>
                )}
              </label>
            </div>
            <div>
              <span
                className="my-2"
                style={{
                  whiteSpace: "break-spaces",
                  color: '#8a8397',
                  fontFamily: '"Plus Jakarta Sans"',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '140%',
                  letterSpacing: '0.07px'
                }}
              >
                {pensionDisabledReason}
              </span>
            </div>
          </div>
          )}

        {/* Holiday Pay Section */}
        <div
          className={`ph-card${isRegularEngagement ? ' ph-card--clickable' : ''}`}
          data-tour="tour-holiday"
          onClick={cardToggleHandler(isRegularEngagement, () => handleHolidayCheckboxChange(!holidayIsChecked))}
        >
        <div className="form-check d-flex align-items-center mr-2 mt-2">
          <input
            className="form-check-input text-dark cursor-pointer"
            type="checkbox"
            id={`holidayPay-${freelancerId}`}
            disabled={!isRegularEngagement}
            checked={isRegularEngagement && currentHoliday.holidaySavings}
            onChange={(e) => handleHolidayCheckboxChange(e.target.checked)}
          />
          <label
            className="form-check-label pl-2 mt-1 cursor-pointer"
            htmlFor={`holidayPay-${freelancerId}`}
          >
           <p style={{
                  color: '#252B37',
                  fontFamily: '"Plus Jakarta Sans"',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 600,
                  lineHeight: '140%',
                  margin: 0,
                  display: 'inline'
                }}>{t('HOLIDAY_PAY')}</p>
            {freelancerName && (
              <span className="ml-2" style={{
                  color: '#252B37',
                  fontFamily: '"Plus Jakarta Sans"',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 600,
                  lineHeight: '140%',
                  margin: 0,
                  display: 'inline'
                }}>({freelancerName})</span>
            )}
          </label>
        </div>
        <div>
          <span
            className="my-2"
           style={{ 
                  whiteSpace: "break-spaces", 
                  color: '#252B37',
                  fontFamily: '"Plus Jakarta Sans"',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 300,
                  lineHeight: '140%',
                  letterSpacing: '0.07px'
                }}
          >
            {!isRegularEngagement
              ? t('AVAILABLE_ONLY_REGULAR_ENGAGEMENT')
              : t('HOLIDAY_PAY_NOTE_190_SEK')
            }
          </span>
        </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default PensionHolidaySection;
